import { create } from 'zustand';

import type { ToastTone } from '../components/ui/toast';

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 5000;

let toastId = 0;
const nextId = () => `toast-${++toastId}`;

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: string;
  action?: ToastAction;
  /** Auto-dismiss in ms. `'infinite'` keeps the toast until the user closes it. Default 5000 (or `'infinite'` for danger). */
  duration?: number | 'infinite';
}

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: ToastAction;
  duration: number | 'infinite';
}

interface ToastStore {
  toasts: ToastItem[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (input) => {
    const id = nextId();
    const tone = input.tone ?? 'info';
    const duration =
      input.duration ?? (tone === 'danger' ? 'infinite' : DEFAULT_DURATION_MS);
    const next: ToastItem = {
      id,
      tone,
      title: input.title,
      description: input.description,
      action: input.action,
      duration,
    };
    set((state) => {
      const merged = [...state.toasts, next];
      // Cap at MAX_TOASTS — oldest first.
      return { toasts: merged.slice(-MAX_TOASTS) };
    });
    return id;
  },
  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

// Imperative API — call anywhere in component code.
type ToastFn = ((input: ToastInput) => string) & {
  info: (title: string, description?: string) => string;
  success: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  danger: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
};

const baseToast = (input: ToastInput) => useToastStore.getState().push(input);

const helper = (tone: ToastTone) => (title: string, description?: string) =>
  useToastStore.getState().push({ tone, title, description });

export const toast: ToastFn = Object.assign(baseToast, {
  info: helper('info'),
  success: helper('success'),
  warning: helper('warning'),
  danger: helper('danger'),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
});
